import { Type } from '@sinclair/typebox';
import type { RouteContract } from '@spfn/core/route';

/**
 * Label Value 타입 정의
 * 모든 타입은 반드시 객체 형태로 저장되어야 함
 */
const LabelValueSchema = Type.Object({
    type: Type.Union([
        Type.Literal('text'),
        Type.Literal('image'),
        Type.Literal('video'),
        Type.Literal('file'),
        Type.Literal('object')
    ]),
    content: Type.Optional(Type.String()), // text type
    url: Type.Optional(Type.String()), // image, video, file types (required for these types but optional in schema)
    alt: Type.Optional(Type.String()), // image type
    width: Type.Optional(Type.Number()), // image type
    height: Type.Optional(Type.Number()), // image type
    thumbnail: Type.Optional(Type.String()), // video type
    duration: Type.Optional(Type.Number()), // video type
    filename: Type.Optional(Type.String()), // file type
    size: Type.Optional(Type.Number()), // file type
    fields: Type.Optional(Type.Any()) // object type - recursive structure
});

/**
 * POST /_cms/values/:labelId - 라벨 값 저장
 * - version: null → Draft 저장 (덮어쓰기)
 * - version: number → Published 버전 생성 (불변)
 */
export const saveValuesContract = {
    method: 'POST' as const,
    path: '/_cms/values/:labelId',
    params: Type.Object({
        labelId: Type.String({ description: '라벨 ID' })
    }),
    body: Type.Object({
        version: Type.Union([
            Type.Null({ description: 'Draft 저장 (덮어쓰기)' }),
            Type.Number({ description: '버전 번호 (불변)', minimum: 1 })
        ]),
        values: Type.Array(
            Type.Object({
                locale: Type.String({ description: '언어 코드 (ko, en, ja)', default: 'ko' }),
                breakpoint: Type.Optional(Type.Union([
                    Type.Literal('sm'),
                    Type.Literal('md'),
                    Type.Literal('lg'),
                    Type.Literal('xl'),
                    Type.Literal('2xl'),
                    Type.Null()
                ], { description: '반응형 브레이크포인트' })),
                value: LabelValueSchema  // 모든 라벨 값은 객체 형태 (type 필드 필수)
            })
        )
    }),
    response: Type.Union([
        Type.Object({
            success: Type.Boolean(),
            saved: Type.Number(),
            version: Type.Union([Type.Null(), Type.Number()])
        }),
        Type.Object({
            error: Type.String()
        })
    ])
} as const satisfies RouteContract;

/**
 * GET /_cms/values/:labelId/:version - 특정 버전의 값 조회
 */
export const getValuesContract = {
    method: 'GET' as const,
    path: '/_cms/values/:labelId/:version',
    params: Type.Object({
        labelId: Type.String({ description: '라벨 ID' }),
        version: Type.String({ description: '버전 번호' })
    }),
    query: Type.Object({
        locale: Type.Optional(Type.String({ description: '언어 코드 (ko, en, ja)' })),
        breakpoint: Type.Optional(Type.String({ description: '반응형 브레이크포인트' }))
    }),
    response: Type.Union([
        Type.Object({
            labelId: Type.Number(),
            version: Type.Number(),
            values: Type.Array(
                Type.Object({
                    id: Type.Number(),
                    locale: Type.String(),
                    breakpoint: Type.Union([Type.String(), Type.Null()]),
                    value: Type.Any(),
                    createdAt: Type.String()
                })
            )
        }),
        Type.Object({
            error: Type.String()
        })
    ])
} as const satisfies RouteContract;